import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  FaCircleCheck,
  FaCircleXmark,
  FaCircleExclamation,
  FaCircleDot,
  FaGears,
  FaChevronDown,
  FaChevronRight,
} from "react-icons/fa6";
import { FaBell } from "react-icons/fa";
import { formatTimestamp } from "./pdActivityUtils";
import styles from "./PagerDutyActivity.module.css";

/**
 * Returns the type-color CSS class for the timeline icon. The icon is the sole
 * per-row marker (there is no separate dot), so it carries the type color:
 *
 * triggered  → EP1 red      declined  → EP1 red
 * workflow   → EP3 blue      escalated → EP2 amber
 * responded  → EP4 green     requested → EP3 blue
 * other      → muted neutral
 */
function iconColorClass(type) {
  switch ((type || "").toLowerCase()) {
    case "triggered":
      return styles.timelineIconTriggered;
    case "responded":
      return styles.timelineIconResponded;
    case "declined":
      return styles.timelineIconDeclined;
    case "escalated":
      return styles.timelineIconEscalated;
    case "requested":
      return styles.timelineIconRequested;
    case "other":
      return styles.timelineIconOther;
    case "workflow":
    default:
      return styles.timelineIconWorkflow;
  }
}

/**
 * Returns the single per-row marker: a type-distinct, type-colored icon (shape
 * AND color signal the event, so the meaning isn't color-only). triggered → bell,
 * workflow → gears, responded → check, declined → x-circle,
 * escalated → exclamation-circle, requested → bell, other → neutral dot.
 */
function IconForType({ type }) {
  const cls = `${styles.timelineTypeIcon} ${iconColorClass(type)}`;
  switch ((type || "").toLowerCase()) {
    case "triggered":
      return <FaBell className={cls} aria-hidden="true" />;
    case "workflow":
      return <FaGears className={cls} aria-hidden="true" />;
    case "responded":
      return <FaCircleCheck className={cls} aria-hidden="true" />;
    case "declined":
      return <FaCircleXmark className={cls} aria-hidden="true" />;
    case "escalated":
      return <FaCircleExclamation className={cls} aria-hidden="true" />;
    case "requested":
      return <FaBell className={cls} aria-hidden="true" />;
    default:
      return <FaCircleDot className={cls} aria-hidden="true" />;
  }
}

IconForType.propTypes = {
  type: PropTypes.string,
};

const TIMELINE_PAGE_SIZE = 15;

/**
 * PagerDutyTimelineList — collapsible section showing the full chronological
 * PagerDuty incident timeline: one row per log entry, mirroring PagerDuty's
 * own incident timeline view.
 *
 * Event types: triggered, workflow, responded, declined, escalated, requested, other.
 *
 * Props:
 *   timeline              {Array}  event objects {type, label, at}
 *   timelineEventCount    {number} override count (from summary); falls back to timeline.length
 */
function PagerDutyTimelineList({ timeline, timelineEventCount }) {
  const [expanded, setExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const total = timelineEventCount ?? timeline.length;
  const visible = showAll ? timeline : timeline.slice(0, TIMELINE_PAGE_SIZE);
  const hiddenCount = timeline.length - TIMELINE_PAGE_SIZE;

  return (
    <section className={styles.sectionBlock} aria-labelledby="pd-timeline-heading">
      <h4 className={styles.sectionHeadingWrapper}>
        <button
          className={styles.sectionToggleBtn}
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          aria-controls="pd-timeline-list"
        >
          <span id="pd-timeline-heading" className={styles.sectionHeading}>
            Timeline
          </span>
          {total > 0 && (
            <span className={styles.sectionCount} aria-label={`${total} event${total !== 1 ? "s" : ""}`}>
              {total}
            </span>
          )}
          <span className={styles.sectionChevron} aria-hidden="true">
            {expanded ? <FaChevronDown /> : <FaChevronRight />}
          </span>
        </button>
      </h4>

      {expanded && (
        <div
          id="pd-timeline-list"
          className={styles.timelineList}
          role="list"
          aria-label="PagerDuty timeline events"
        >
          {timeline.length === 0 ? (
            <div className={styles.emptySection}>
              <FaBell className={styles.emptyIcon} aria-hidden="true" />
              <p>No timeline events recorded for this incident.</p>
            </div>
          ) : (
            <>
              {visible.map((event, idx) => (
                <div
                  key={`${event.type}-${event.at}-${idx}`}
                  className={styles.timelineRow}
                  role="listitem"
                >
                  <IconForType type={event.type} />
                  <div className={styles.timelineContent}>
                    <span className={styles.timelineLabel}>{event.label || "Event"}</span>
                    <span className={styles.timelineTime}>{formatTimestamp(event.at)}</span>
                  </div>
                </div>
              ))}

              {!showAll && hiddenCount > 0 && (
                <button
                  className={styles.showMoreBtn}
                  onClick={() => setShowAll(true)}
                  aria-label={`Show ${hiddenCount} more timeline event${hiddenCount !== 1 ? "s" : ""}`}
                >
                  Show {hiddenCount} more event{hiddenCount !== 1 ? "s" : ""}
                </button>
              )}
              {showAll && timeline.length > TIMELINE_PAGE_SIZE && (
                <button
                  className={styles.showMoreBtn}
                  onClick={() => setShowAll(false)}
                  aria-label="Show fewer timeline events"
                >
                  Show fewer events
                </button>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}

PagerDutyTimelineList.propTypes = {
  timeline: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      label: PropTypes.string,
      at: PropTypes.string,
    })
  ).isRequired,
  timelineEventCount: PropTypes.number,
};

export default PagerDutyTimelineList;
